import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { Link } from "react-router-dom";

import Header from "../component/layout/header";
import PageHeader from "../component/layout/pageheader";
import FooterSection from "../component/layout/footer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import Pagination from "../component/section/pagination";
import FilterSearchSection from "../component/section/filtersearch";
import { convertImageToWebP } from "../utils/convertToWebp";
import axios from "axios";
import { toast } from "react-toastify";
import { SyncLoader } from "react-spinners";
import "react-quill/dist/quill.snow.css";

const ReactQuill = lazy(() => import("react-quill"));

// Error Boundary Component
class ErrorBoundary extends React.PureComponent {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary:", error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <h3>Something went wrong. Please refresh.</h3>;
    }
    return this.props.children;
  }
}

const MAX_IMAGES = 3;

const quillModulesTitle = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ header: [1, 2, 3, false] }],
    ["link"],
  ],
};
const quillModulesDesc = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
  ],
};

const StoryPage = () => {
  const [titleHtml, setTitleHtml] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [images, setImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [errors, setErrors] = useState({});
  const [videoLoading, setVideoLoading] = useState(false); // New state for video loader

  const fileInputRef = useRef(null);
  const [stories, setStories] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(false);


  const token = useMemo(() => localStorage.getItem("token"), []);

  const triggerFileInput = useCallback(() => {
    if (images.length < MAX_IMAGES) fileInputRef.current?.click();
  }, [images.length]);

  const removeImage = useCallback((index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const extractYoutubeID = useCallback((url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  }, []);

  const handleImageUpload = useCallback(
    async (e) => {
      const selectedFiles = Array.from(e.target.files || []).slice(
        0,
        MAX_IMAGES - images.length
      );
      if (!selectedFiles.length) return;

      setIsUploadingImage(true);

      for (const file of selectedFiles) {
        try {
          let uploadFile = file;
          if (typeof convertImageToWebP === "function") {
            try {
              const webpBlob = await convertImageToWebP(file);
              uploadFile = new File(
                [webpBlob],
                file.name.replace(/\.[^/.]+$/, "") + ".webp",
                { type: "image/webp" }
              );
            } catch {
              uploadFile = file;
            }
          }

          const fd = new FormData();
          fd.append("image", uploadFile);

          const res = await axios.post(
            `${process.env.REACT_APP_MAIN_SITE_URL || ""}/upload-image`,
            fd,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          );

          const imageUrl = res?.data?.data?.url || res?.data?.url;
          if (!imageUrl) throw new Error("No image URL returned");

          setImages((prev) =>
            [...prev, { url: imageUrl }].slice(0, MAX_IMAGES)
          );
          toast.success("Image uploaded");
        } catch (err) {
          console.error("Image upload error", err);
          toast.error("Image upload failed");
        }
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsUploadingImage(false);
    },
    [token, images.length]
  );

  const handleSubmitStory = useCallback(async () => {
    const newErrors = {};
    if (!titleHtml.trim()) newErrors.title = "Title is required.";
    if (!descriptionHtml.trim())
      newErrors.description = "Description is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix form errors");
      return;
    }

    setErrors({});

    try {
      const payload = {
        title: titleHtml,
        description: descriptionHtml,
        images: images.map((i) => i.url),
        youtube_link: youtubeLink.trim(),
      };

      const res = await axios.post(
        `${process.env.REACT_APP_MAIN_SITE_URL || ""}/story/store`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      toast.success("Story created");

      const newStory = res.data.data;

      setStories((prev) => [newStory, ...prev]);

      setTitleHtml("");
      setDescriptionHtml("");
      setImages([]);
      setYoutubeLink("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to create story");
    }
  }, [titleHtml, descriptionHtml, images, youtubeLink, token]);

  const fetchStories = useCallback(
    async (p = 1) => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_MAIN_SITE_URL}/story`,
          {
            params: { page: p },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );

        setStories(res.data.data || []);
        setMeta(res.data.meta || { current_page: 1, last_page: 1 });

      } catch (err) {
        console.error("Failed to fetch stories", err);
        toast.error("Failed to load stories");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchStories(1);
  }, [fetchStories]);

  const goToPage = useCallback(
    (p) => {
      if (p < 1 || p > meta.last_page) return;
      fetchStories(p);
    },
    [fetchStories, meta.last_page]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this story?")) return;

      try {
        await axios.post(
          `${process.env.REACT_APP_MAIN_SITE_URL}/story/delete/${id}`,
          {},
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }
        );
        toast.success("Deleted successfully");
        setStories((prev) => prev.filter((s) => s.id !== id));
      } catch (err) {
        console.error(err);
        toast.error("Delete failed");
      }
    },
    [token]
  );

  return (
    <ErrorBoundary>
      <div>
        <Header />
        <PageHeader title="Blog" />

        {loading && (
          <div className="fullscreen-loader">
            <SyncLoader size={12} color="#DF314D" />
          </div>
        )}

        <section className="blog-section padding-tb">
          <div className="container">
            <div className="main-blog">
              <div className="row">
                <div className="col-lg-8 col-12">
                  <div className="blog-wrapper">
                    {/* Story Creation Card */}
                    <div className="story-card">
                      <div className="story-card-inner">
                        {/* Image Upload Section */}
                        <div className="image-upload-section">
                          <div className="image-upload-grid">
                            {images.map((img, idx) => (
                              <div
                                key={idx}
                                className="image-preview-container"
                              >
                                <img
                                  src={img.url}
                                  alt="uploaded"
                                  loading="lazy"
                                  className="image-preview"
                                />
                                <button
                                  onClick={() => removeImage(idx)}
                                  className="remove-image-btn"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            {images.length < MAX_IMAGES && (
                              <div
                                onClick={triggerFileInput}
                                className="add-image-btn"
                              >
                                {isUploadingImage ? "..." : "+"}
                              </div>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            multiple
                            className="file-input"
                            onChange={handleImageUpload}
                          />
                        </div>

                        {/* Content Section */}
                        <div className="content-section">
                          <h3 className="title-editor">
                            <Suspense fallback={<div>Loading editor...</div>}>
                              <ReactQuill
                                theme="snow"
                                value={titleHtml || ""}
                                onChange={setTitleHtml}
                                placeholder="Enter story title"
                                modules={quillModulesTitle}
                              />
                            </Suspense>
                          </h3>
                          {errors.title && (
                            <p className="error-message">{errors.title}</p>
                          )}
                          <div className="description-editor">
                            <Suspense fallback={<div>Loading editor...</div>}>
                              <ReactQuill
                                theme="snow"
                                value={descriptionHtml || ""}
                                onChange={setDescriptionHtml}
                                placeholder="Enter story description"
                                modules={quillModulesDesc}
                              />
                            </Suspense>
                          </div>
                          {errors.description && (
                            <p className="error-message">
                              {errors.description}
                            </p>
                          )}



                          <div className="youtube-input">
                            <label htmlFor="" className="">
                              Enter Youtube Link Or Facebook 
                            </label>
                            <input
                              type="text"
                              value={youtubeLink}
                              onChange={(e) => setYoutubeLink(e.target.value)}
                              placeholder="Enter YouTube video link"
                              className="youtube-input-field"
                            />
                          </div>





                          {youtubeLink && extractYoutubeID(youtubeLink) && (
                            <div className="youtube-preview">
                              {videoLoading && (
                                <div className="video-loader">
                                  <SyncLoader size={8} color="#DF314D" />
                                </div>
                              )}
                              <iframe
                                width="100%"
                                height="200"
                                src={`https://www.youtube.com/embed/${extractYoutubeID(
                                  youtubeLink
                                )}`}
                                title="YouTube video preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                onLoad={() => setVideoLoading(false)}
                                onLoadStart={() => setVideoLoading(true)}
                              />
                            </div>
                          )}
                        </div>

                        {/* Submit Button */}
                        <div className="action-section">
                          <button
                            onClick={handleSubmitStory}
                            className="submit-btn"
                            disabled={isUploadingImage}
                          >
                            {isUploadingImage ? "Uploading..." : "Create"}{" "}
                            <i className="icofont-double-right"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Stories List */}
                    {stories.map((s) => (
                      <div
                        key={s.id}
                        className={
                          s.youtube_link ? "post-item video-post" : "post-item"
                        }
                      >
                        <div className="post-item-inner">
                          {s.youtube_link && (
                            <div className="post-thumb">
                              <div className="embed-responsive">
                                <VideoWithLoader url={s.youtube_link} />
                              </div>
                            </div>
                          )}

                          {!s.youtube_link &&
                            s.images &&
                            s.images.length > 0 && (
                              <div className="post-thumb">
                                <Swiper
                                  spaceBetween={10}
                                  centeredSlides
                                  autoplay={{
                                    delay: 4000,
                                    disableOnInteraction: false,
                                  }}
                                  navigation={{
                                    prevEl: ".blog-slider-prev",
                                    nextEl: ".blog-slider-next",
                                  }}
                                  loop={s.images.length > 1}
                                  modules={[Autoplay, Navigation]}
                                  className="mySwiper"
                                >
                                  {s.images.map((imgUrl, i) => (
                                    <SwiperSlide key={i}>
                                      <img
                                        src={imgUrl}
                                        alt={`story-${s.id}-${i}`}
                                        loading="lazy"
                                        style={{ width: "100%" }}
                                      />
                                    </SwiperSlide>
                                  ))}
                                </Swiper>
                              </div>
                            )}

                          <div className="post-content">
                            <span className="meta">
                              By {s.user?.name || "ME"} ·{" "}
                              {new Date(s.created_at).toLocaleDateString()}
                            </span>
                            <h3 dangerouslySetInnerHTML={{ __html: s.title }} />
                            <p>{s.excerpt}</p>

                            <div className="blog-footer">
                              <div className="left">
                                <Link to={`/story-details/${s.id}`} className="lab-btn">
                                  Read More <i className="icofont-double-right"></i>
                                </Link>
                              </div>
                              <div className="right">
                                {s.can_delete && (
                                  <button
                                    onClick={() => handleDelete(s.id)}
                                    className="btn btn-link"
                                  >
                                    <i className="icofont-trash"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <Pagination goToPage={goToPage} meta={meta} />
                  </div>
                </div>

                <div className="col-lg-4 col-12">
                  <aside className="mt-5 mt-lg-0">
                    <FilterSearchSection />
                  </aside>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterSection />
      </div>
    </ErrorBoundary>
  );
};

// Separate Component for Video with Loader
const VideoWithLoader = ({ url }) => {
  const [loading, setLoading] = useState(true);

  const extractYoutubeID = useCallback((url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  }, []);

  return (
    <div className="video-wrapper" style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#00000030",
            zIndex: 10,
          }}
        >
          <SyncLoader size={8} color="#DF314D" />
        </div>
      )}
      <iframe
        width="100%"
        height="350"
        src={`https://www.youtube.com/embed/${extractYoutubeID(url)}`}
        title="YouTube video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={() => setLoading(false)}
      />
    </div>
  );
};

export default React.memo(StoryPage);
